<template>
  <tr
    v-if="bluredOrNotHidden"
    :class="{ active: isActive, hovered: showActions }"
    class="draggableBlock"
    data-block="Rating"
    @mouseover="mouseOver"
    @mouseleave="mouseOut"
    @click.stop="() => click()"
  >
    <td
      :class="{
        'block-borders': showDragBorder,
        'dragged-opacity': isCurrentElementDragged,
      }"
      :style="{ padding: padding, backgroundColor: bgColor }"
      style="word-break: break-word"
      :align="align"
    >
      <blockActions
        v-if="showActions"
        :column-padding-x="columnPaddingX"
        :column-width="columnWidth"
        :disable-up-movement="disableUpMovement"
        :disable-down-movement="disableDownMovement"
        :block-height="blockHeight"
        :block-width="blockWidth"
        :is-active="isActive"
        :is-last-column="isLastColumn"
        :section-index="sectionIndex"
        :column-index="columnIndex"
        :block-index="blockIndex"
        :show-paste="showPaste"
        :copied="copied"
        @duplicate="copyBlock"
        @remove="deleteBlock"
        @moveUp="moveUp"
        @moveDown="moveDown"
        @copy="copyToClipboard"
      ></blockActions>
      <div :id="id" :style="{ ...hiddenStyle, textAlign: align }">
        <p v-if="showQuestion && questionText" :style="questionStyle">
          {{ questionText }}
        </p>
        <div :style="{ textAlign: align }">
          <span v-if="hasLeftGroup" :style="captionGroupStyle">
            <span :style="inlineCaptionStyle">{{ leftCaption }}</span>
            <span :style="segmentWrapStyle">
              <a
                v-if="isImageMode"
                :href="getSegmentHref(0)"
                :style="imageLinkStyle"
                @click.stop.prevent="() => click()"
              >
                <img
                  :src="readChildAttr(0, 'src') || dummySrc"
                  :alt="readChildAttr(0, 'alt')"
                  :style="readChildAttr(0, 'src') ? imageStyle : dummyImgStyle"
                />
              </a>
              <a
                v-else
                :href="getSegmentHref(0)"
                :style="
                  hover && hoveredSegment === segmentList[0]
                    ? textHoveredStyle
                    : textSegmentStyle
                "
                @mouseenter="hoveredSegment = segmentList[0]"
                @mouseleave="hoveredSegment = null"
                @click.stop.prevent="() => click()"
              >
                {{ readChildAttr(0, "label") }}
              </a>
              <div
                v-if="isImageMode && showImageLabel"
                :style="imageLabelStyle"
              >
                {{ readChildAttr(0, "label") }}
              </div>
            </span>
          </span>
          <span
            v-for="i in middleIndices"
            :key="`m-${i}`"
            :style="segmentWrapStyle"
          >
            <a
              v-if="isImageMode"
              :href="getSegmentHref(i)"
              :style="imageLinkStyle"
              @click.stop.prevent="() => click()"
            >
              <img
                :src="readChildAttr(i, 'src') || dummySrc"
                :alt="readChildAttr(i, 'alt')"
                :style="readChildAttr(i, 'src') ? imageStyle : dummyImgStyle"
              />
            </a>
            <a
              v-else
              :href="getSegmentHref(i)"
              :style="
                hover && hoveredSegment === segmentList[i]
                  ? textHoveredStyle
                  : textSegmentStyle
              "
              @mouseenter="hoveredSegment = segmentList[i]"
              @mouseleave="hoveredSegment = null"
              @click.stop.prevent="() => click()"
            >
              {{ readChildAttr(i, "label") }}
            </a>
            <div v-if="isImageMode && showImageLabel" :style="imageLabelStyle">
              {{ readChildAttr(i, "label") }}
            </div>
          </span>
          <span v-if="hasRightGroup" :style="captionGroupStyle">
            <span :style="segmentWrapStyle">
              <a
                v-if="isImageMode"
                :href="getSegmentHref(lastIndex)"
                :style="imageLinkStyle"
                @click.stop.prevent="() => click()"
              >
                <img
                  :src="readChildAttr(lastIndex, 'src') || dummySrc"
                  :alt="readChildAttr(lastIndex, 'alt')"
                  :style="
                    readChildAttr(lastIndex, 'src') ? imageStyle : dummyImgStyle
                  "
                />
              </a>
              <a
                v-else
                :href="getSegmentHref(lastIndex)"
                :style="
                  hover && hoveredSegment === segmentList[lastIndex]
                    ? textHoveredStyle
                    : textSegmentStyle
                "
                @mouseenter="hoveredSegment = segmentList[lastIndex]"
                @mouseleave="hoveredSegment = null"
                @click.stop.prevent="() => click()"
              >
                {{ readChildAttr(lastIndex, "label") }}
              </a>
              <div
                v-if="isImageMode && showImageLabel"
                :style="imageLabelStyle"
              >
                {{ readChildAttr(lastIndex, "label") }}
              </div>
            </span>
            <span :style="inlineCaptionStyle">{{ rightCaption }}</span>
          </span>
        </div>
        <div
          v-if="
            showCaptions &&
            captionLayout === 'below' &&
            (leftCaption || rightCaption)
          "
          :style="captionsContainerStyle"
        >
          <table
            class="nps-labels"
            role="presentation"
            cellpadding="0"
            cellspacing="0"
            border="0"
            :style="captionsTableStyle"
          >
            <tbody>
              <tr>
                <td align="left" :style="captionStyle">
                  {{ leftCaption }}
                </td>
                <td align="right" :style="captionStyle">
                  {{ rightCaption }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </td>
  </tr>
</template>

<script lang="ts" setup>
  import blockProperties from "@/composables/useBlockProperties";
  import blockActions from "./blockActions.vue";
  import { substituteRatingUrl } from "@/utils/substituteRatingUrl";
  import { TopolRatingBlock } from "@/schemas/template/blocks/ratingBlockSchema";
  import { useMjmlStore } from "@/stores/mjmlStore";

  import { computed, shallowRef, StyleValue } from "vue";

  const mjmlStore = useMjmlStore();

  const props = defineProps<{
    block: TopolRatingBlock;
    columnIndex: number;
    sectionIndex: number;
    blockIndex: number;
    isLocked: boolean;
  }>();

  const {
    showActions,
    showPaste,
    blockHeight,
    blockWidth,
    id,
    bluredOrNotHidden,
    hiddenStyle,
    isActive,
    disableUpMovement,
    disableDownMovement,
    columnPaddingX,
    columnWidth,
    isCurrentElementDragged,
    showDragBorder,
    isLastColumn,
    copied,
    mouseOver,
    mouseOut,
    click,
    copyBlock,
    deleteBlock,
    moveUp,
    moveDown,
    copyToClipboard,
  } = blockProperties(props);

  const a = computed(() => props.block.attributes);
  const qa = computed(() => (props.block as any).questionAttributes || {});
  const ca = computed(() => (props.block as any).captionsAttributes || {});
  const sa = computed(() => (props.block as any).segmentsAttributes || {});
  const il = computed(() => (props.block as any).imageLabelAttributes || {});

  const align = computed(() => a.value["align"] || "center");
  const padding = computed(() => a.value["padding"] || "10px 10px 10px 10px");
  const bgColor = computed(() => a.value["container-background-color"] || "");

  const segmentFontFamily = computed(
    () => sa.value["font-family"] || "Helvetica, Arial, sans-serif"
  );

  function readTranslatableProp(prop: string): string {
    return mjmlStore.getSpecificBlockProp(props.block, prop);
  }

  function readChildAttr(childIndex: number, attr: string): string {
    return mjmlStore.getSpecificBlockChildAttr(props.block, childIndex, attr);
  }

  function getSegmentHref(childIndex: number): string {
    const mode = (props.block as any)?.url?.mode ?? "pattern";
    if (mode === "individual") {
      return readChildAttr(childIndex, "href");
    }
    return substituteRatingUrl(
      readTranslatableProp("urlPattern"),
      readChildAttr(childIndex, "value")
    );
  }

  const showQuestion = computed(() => qa.value.show !== false);
  const questionText = computed(() => readTranslatableProp("questionText"));
  const showCaptions = computed(() => ca.value.show !== false);
  const leftCaption = computed(() => readTranslatableProp("captionTextLeft"));
  const rightCaption = computed(() => readTranslatableProp("captionTextRight"));
  const captionLayout = computed<"below" | "inline">(() => {
    if (mjmlStore.isMobileFirstEnabled && mjmlStore.isMobileView) {
      return (
        (ca.value.layoutMobile as "below" | "inline") ||
        (ca.value.layout as "below" | "inline") ||
        "below"
      );
    }
    return (ca.value.layout as "below" | "inline") || "below";
  });
  const showImageLabel = computed(() => il.value.show === true);

  const gap = computed(() => sa.value.gap || "4px");
  const segmentSize = computed(() => sa.value.size || "32px");
  const segmentSizeNum = computed(() =>
    parseInt(String(segmentSize.value).replace("px", ""), 10)
  );
  const isImageMode = computed(() => sa.value.mode === "image");

  const segmentList = computed(() => {
    return Array.isArray(props.block.children)
      ? props.block.children.filter(
          (child: any) =>
            typeof child === "object" && typeof child.attributes === "object"
        )
      : [];
  });

  const lastIndex = computed(() => segmentList.value.length - 1);
  const hasLeftGroup = computed(
    () =>
      showCaptions.value &&
      captionLayout.value === "inline" &&
      !!leftCaption.value &&
      segmentList.value.length > 0
  );
  const hasRightGroup = computed(
    () =>
      showCaptions.value &&
      captionLayout.value === "inline" &&
      !!rightCaption.value &&
      segmentList.value.length > 0
  );
  const middleIndices = computed(() => {
    const start = hasLeftGroup.value ? 1 : 0;
    const end = hasRightGroup.value
      ? segmentList.value.length - 1
      : segmentList.value.length;
    return Array.from({ length: end - start }, (_, k) => start + k);
  });

  function textStyle(group: any): Record<string, any> {
    return {
      color: group.color,
      "font-family": group["font-family"],
      "font-size": group["font-size"],
      "font-weight": group["font-weight"],
      "font-style": group["font-style"],
      "line-height": group["line-height"],
      "text-transform": group["text-transform"],
      "text-decoration": group["text-decoration"],
      "letter-spacing": group["letter-spacing"],
    };
  }

  const hover = computed(() => (props.block as any).segmentsHover);
  const hoveredSegment = shallowRef<any>(null);

  function borderWidth(border: string | undefined): number {
    const m = (border || "").match(/^(\d+)px/);
    return m ? parseInt(m[1], 10) : 0;
  }
  const segmentBorderWidth = computed(() => borderWidth(sa.value["border"]));
  const segmentInnerHeight = computed(
    () => `${segmentSizeNum.value - 2 * segmentBorderWidth.value}px`
  );
  const segmentHoveredInnerHeight = computed(() => {
    const hb = (hover.value || {})?.border;
    if (!hb) return segmentInnerHeight.value;
    return `${segmentSizeNum.value - 2 * borderWidth(hb)}px`;
  });

  const isAutoWidth = computed(() => sa.value.autoWidth === true);

  const textSegmentStyle = computed<StyleValue>(() => {
    const base: Record<string, unknown> = {
      display: "inline-block",
      height: segmentSize.value,
      "line-height": segmentInnerHeight.value,
      background: sa.value["background-color"] || "#ffffff",
      border: sa.value["border"] || "1px solid #1a1a1a",
      "border-radius": sa.value["border-radius"] || "8px",
      "text-align": "center",
      "vertical-align": "middle",
      "box-sizing": "border-box",
      "text-decoration": "none",
      cursor: "pointer",
      color: sa.value.color || "#1a1a1a",
      "font-family": segmentFontFamily.value,
      "font-size": sa.value["font-size"] || "14px",
      "font-weight": sa.value["font-weight"] || "normal",
    };
    if (isAutoWidth.value) {
      base["min-width"] = segmentSize.value;
      base.padding = "0 12px";
    } else {
      base.width = segmentSize.value;
    }
    return base as StyleValue;
  });

  const textHoveredStyle = computed<StyleValue>(() => {
    const base = textSegmentStyle.value as Record<string, unknown>;
    const h = (hover.value || {}) as Record<string, string>;
    return {
      ...base,
      ...(h["background-color"] ? { background: h["background-color"] } : {}),
      ...(h.color ? { color: h.color } : {}),
      ...(h.border
        ? { border: h.border, "line-height": segmentHoveredInnerHeight.value }
        : {}),
    };
  });

  const imageLinkStyle = computed<StyleValue>(() => ({
    display: "inline-block",
    "text-decoration": "none",
    cursor: "pointer",
  }));

  const imageStyle = computed<StyleValue>(() => {
    const base: Record<string, unknown> = {
      display: "block",
      height: segmentSize.value,
      border: sa.value["border"] || "0",
      "border-radius": sa.value["border-radius"] || "0",
      "box-sizing": "border-box",
    };
    if (isAutoWidth.value) {
      // Height fixed, width follows source aspect ratio
      base.width = "auto";
    } else {
      base.width = segmentSize.value;
      base["object-fit"] = "contain";
    }
    return base as StyleValue;
  });

  const awsFolder = computed(() => import.meta.env.VITE_APP_AWS_FOLDER);
  const dummySrc = computed(
    () => `${awsFolder.value}/assets/images/merge-tags/dummy.svg`
  );
  const dummyImgStyle = computed<StyleValue>(() => ({
    display: "block",
    width: segmentSize.value,
    height: segmentSize.value,
    border: sa.value["border"] || "0",
    "border-radius": sa.value["border-radius"] || "0",
    "box-sizing": "border-box",
    background: "#D4D5D9",
    "object-fit": "scale-down",
  }));

  const segmentWrapStyle = computed<StyleValue>(() => ({
    display: "inline-block",
    "vertical-align": "top",
    margin: gap.value,
    "text-align": "center",
  }));

  const questionStyle = computed<StyleValue>(() => ({
    margin: 0,
    "line-height": "1.3",
    padding: qa.value.padding || "0px 0px 12px 0px",
    "text-align": align.value,
    ...textStyle(qa.value),
  }));

  const imageLabelStyle = computed<StyleValue>(() => ({
    "padding-top": "6px",
    ...textStyle(il.value),
  }));

  const captionsContainerStyle = computed<StyleValue>(() => ({
    padding: ca.value.padding || "8px 0px 0px 0px",
  }));

  const captionsTableStyle = computed<StyleValue>(() => ({
    "border-collapse": "collapse",
    margin: "0 auto",
    width: "100%",
  }));

  const captionStyle = computed<StyleValue>(() => ({
    padding: 0,
    ...textStyle(ca.value),
  }));

  const inlineCaptionStyle = computed<StyleValue>(() => ({
    display: "inline-block",
    "vertical-align": "top",
    height: segmentSize.value,
    margin: gap.value,
    ...textStyle(ca.value),
    "line-height": segmentSize.value,
  }));

  const captionGroupStyle = computed<StyleValue>(() => ({
    display: "inline-block",
    "vertical-align": "top",
    "white-space": "nowrap",
  }));
</script>
